<template>
	<q-modal
		v-model="show_preview"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn flat round dense @click="show_preview = false" icon="fas fa-times" />
				<q-toolbar-title>
					{{ $t(game_name) }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding">
				<div class="row q-pa-xs">
					<p class="col-12 text-bold">{{ $t('game_preview') }}</p>
					<div v-if="!game_finished" class="row col-12">
						<p v-if="max_errors !== null && max_errors >= 0" class="col-12 text-center">
							{{ `${$t('errors')}: ${qty_errors} / ${max_errors} ` }}
						</p>
						<div class="col-6">
							<div
								v-for="(question, index) in options.questions"
								:key="`q-${index}`"
								class="q-ma-md invisible-border cursor-pointer"
								:class="getQuestionStyle(question)"
								@click="
									question.selected
										? clearQuestion(question)
										: question.correct === undefined
										? selectQuestion(question.value)
										: null
								"
							>
								<q-card>
									<q-card-title class="text-center">
										{{ question.value }}
									</q-card-title>
								</q-card>
							</div>
						</div>

						<div class="col-6">
							<div
								v-for="(answer, index) in options.answers"
								:key="`a-${index}`"
								class="q-ma-md invisible-border cursor-pointer"
								:class="getAnswerStyle(answer)"
								@click="
									answer.selected
										? clearAnswer(answer)
										: answer.correct === undefined
										? selectAnswer(answer.value)
										: null
								"
							>
								<q-card>
									<q-card-title class="text-center">
										{{ answer.value }}
									</q-card-title>
								</q-card>
							</div>
						</div>
					</div>

					<div class="col-10 q-mt-lg" v-if="game_finished">
						<p class="text-bold q-mt-sm">{{ $t('result') }}</p>

						<q-slider
							disable
							markers
							snap
							v-model="user_score"
							:color="game_finished ? getSliderColor() : 'blue-6'"
							:min="0"
							:max="number_of_choices"
						/>

						<p class="q-mt-sm" v-if="max_errors >= 0">
							{{ `${$t('errors')}: ${qty_errors} / ${max_errors}` }}
						</p>

						<p v-if="user_won">{{ $t(success_message) }}</p>
						<p v-else>{{ $t(failure_message) }}</p>
					</div>
				</div>

				<div class="col-xs-12 q-mt-md">
					<div class="row justify-end">
						<q-btn
							color="secondary"
							:label="$t('back')"
							@click="show_preview = false"
						/>
						<q-btn
							v-if="game_finished"
							color="primary"
							class="q-ml-md"
							:label="$t('play_again')"
							@click="resetGame"
						/>
					</div>
				</div>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
export default {
	name: 'AdminGameFindThePairsPreview',

	props: ['show_game_preview', 'game_name', 'game_words', 'game_options', 'game_max_errors'],

	data() {
		return {
			options: { questions: [], answers: [] },
			selected_question: '',
			selected_answer: '',
			words: [],
			max_errors: null,
			qty_errors: 0,
			game_finished: false,
			number_of_choices: 0,
			correct_choices: 0,
			user_score: 0,
			user_won: false,
			success_message: `${this.game_name}_success_message`,
			failure_message: `${this.game_name}_failure_message`,
		}
	},

	computed: {
		show_preview: {
			get() {
				return this.show_game_preview
			},
			set() {
				this.$emit('hide_preview', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '90vw'
			else return '75vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '50vw'
		},
	},

	methods: {
		getQuestionStyle(question) {
			if (question.selected && question.correct === undefined) return 'selected'
			if (question.correct === true) return 'correct'
			if (question.correct === false) return 'incorrect shake'
			else return ''
		},

		getAnswerStyle(answer) {
			if (answer.selected && answer.correct === undefined) return 'selected'
			if (answer.correct) return 'correct'
			if (answer.correct === false) return 'incorrect shake'
			if (this.selected_question.length && !answer.selected && answer.correct === undefined)
				return 'cursor-pointer'
			else return ''
		},

		getSliderColor() {
			let medium = Math.ceil(this.number_of_choices / 2)

			if (this.correct_choices - this.qty_errors < medium) return 'red-6'
			else if (this.correct_choices - this.qty_errors == medium) return 'blue-6'
			else return 'green-6'
		},

		clearQuestion(question) {
			question.selected = false
			this.selected_question = ''
		},

		clearAnswer(answer) {
			answer.selected = false
			this.selected_answer = ''
		},

		selectQuestion(question) {
			if (this.selected_question.length && !this.selected_answer.length) return

			if (this.selected_question.length) {
				const optionsQuestion = this.options.questions.find(
					(q) => q.value === this.selected_question
				)
				return (optionsQuestion.selected = false)
			}

			const optionsQuestion = this.options.questions.find((q) => q.value === question)
			optionsQuestion.selected = true
			this.selected_question = question
			this.checkUserChoice()
		},

		selectAnswer(answer) {
			if (this.selected_answer.length && !this.selected_question.length) return

			if (this.selected_answer.length) {
				const optionsAnswer = this.options.answers.find(
					(a) => a.value === this.selected_answer
				)
				return (optionsAnswer.selected = false)
			}

			const optionsAnswer = this.options.answers.find((a) => a.value === answer)
			optionsAnswer.selected = true
			this.selected_answer = answer
			this.checkUserChoice()
		},

		checkUserChoice() {
			const selectedQuestion = this.options.questions.find(
				(q) => q.value === this.selected_question
			)
			const selectedAnswer = this.options.answers.find(
				(a) => a.value === this.selected_answer
			)

			if (!selectedQuestion || !selectedAnswer) return

			const choice = this.words.find((w) => w.question === selectedQuestion.value)
			if (choice) {
				if (selectedAnswer.value === choice.answer) {
					this.correct_choices += 1
					selectedQuestion.correct = true
					selectedAnswer.correct = true
					selectedQuestion.selected = false
					selectedAnswer.selected = false
				} else {
					selectedQuestion.correct = false
					selectedAnswer.correct = false
					this.qty_errors += 1
					setTimeout(() => {
						selectedQuestion.correct = undefined
						selectedQuestion.selected = false
						selectedAnswer.correct = undefined
						selectedAnswer.selected = false
					}, 1000)
				}
			}
			this.selected_answer = ''
			this.selected_question = ''

			if (this.correct_choices >= this.words.length) {
				setTimeout(() => {
					this.checkResults()
				}, 1000)
			}
		},

		checkResults() {
			this.game_finished = true
			setTimeout(() => {
				this.user_score = this.correct_choices - this.qty_errors
				this.user_won =
					this.max_errors !== -1
						? this.qty_errors > this.max_errors
							? false
							: true
						: this.correct_choices >= this.words.length
			}, 750)
		},

		resetGame() {
			this.options.questions = this.game_options.questions
				.map((q) => ({
					...q,
					selected: false,
					correct: undefined,
					position: Math.floor(Math.random() * 100),
				}))
				.sort((a, b) => b.position - a.position)
			this.options.answers = this.game_options.answers
				.map((a) => ({
					...a,
					selected: false,
					correct: undefined,
					position: Math.floor(Math.random() * 100),
				}))
				.sort((a, b) => b.position - a.position)
			this.correct_choices = 0
			this.qty_errors = 0
			this.user_score = 0
			this.user_won = false

			this.game_finished = false
		},

		buildGame() {
			this.options.questions = this.game_options.questions
				.map((q) => ({ ...q, position: Math.floor(Math.random() * 100) }))
				.sort((a, b) => b.position - a.position)
			this.options.answers = this.game_options.answers
				.map((a) => ({ ...a, position: Math.floor(Math.random() * 100) }))
				.sort((a, b) => b.position - a.position)

			this.words = this.game_words
			this.number_of_choices = this.words.length
			this.max_errors = this.game_max_errors !== null ? this.game_max_errors : -1
			this.qty_errors = 0
		},
	},

	mounted() {
		this.buildGame()
	},
}
</script>

<style lang="stylus">
.invisible-border
  border 2px solid transparent

.selected
  transition all 0.75s ease
  border 2px solid #386cff
  background #93cdff

.correct
  background #80db69
  border 2px solid #35ad35
  transition all 0.75s ease

.incorrect
  transition all 0.75s ease
  background #ff6666
  border 2px solid #d93333

.shake
  animation shake 1s

@keyframes shake {
  0% { transform: translate(2px, 2px) rotate(0deg); }
  10% { transform: translate(-1px, -2px) rotate(-1deg); }
  20% { transform: translate(-4px, 0px) rotate(1deg); }
  30% { transform: translate(4px, 3px) rotate(0deg); }
  40% { transform: translate(2px, -2px) rotate(1deg); }
  50% { transform: translate(-2px, 3px) rotate(-1deg); }
  60% { transform: translate(-4px, 2px) rotate(0deg); }
  70% { transform: translate(4px, 2px) rotate(-1deg); }
  80% { transform: translate(-1px, -1px) rotate(1deg); }
  90% { transform: translate(1px, 2px) rotate(0deg); }
  100% { transform: translate(1px, -2px) rotate(-1deg); }
}
</style>
